import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProAccess } from '@/hooks/useProAccess';

const POPUP_INTERVAL_MS = 180000; // 180 secondes (3 minutes)

interface PremiumUpgradePopupProps {
  forceShow?: boolean;
  onClose?: () => void;
}

const PremiumUpgradePopup = ({ forceShow = false, onClose }: PremiumUpgradePopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isProAccount, loading } = useProAccess();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    
    // Ne pas afficher pour les comptes Pro
    if (isProAccount) return;

    if (forceShow) {
      setIsOpen(true);
      return;
    }

    const showPopup = () => {
      // Vérifier qu'aucun autre dialogue n'est ouvert avant d'afficher
      const hasOpenDialog = document.querySelector('[role="dialog"][data-state="open"]');
      if (hasOpenDialog) return;
      setIsOpen(true);
    };

    // Premier affichage après 36s
    const initialTimer = setTimeout(showPopup, 36000);

    // Puis toutes les 60s
    const interval = setInterval(showPopup, POPUP_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isProAccount, loading, forceShow]);

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleSubscribe = () => {
    setIsOpen(false);
    navigate('/account');
  };

  if (loading || isProAccount) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <DialogContent className="sm:max-w-md z-40">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-nautic-gold/20 flex items-center justify-center">
              <Crown className="h-8 w-8 text-nautic-gold" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Passez à l'abonnement Premium !
          </DialogTitle>
          <DialogDescription className="text-center space-y-3 pt-2">
            <p>
              Débloquez toutes les fonctionnalités de NautiCoach et profitez d'une expérience complète :
            </p>
            <ul className="text-left space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-nautic-gold flex-shrink-0" />
                <span>Inventaire illimité (au lieu de 5 articles)</span>
              </li>
              <li className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-nautic-gold flex-shrink-0" />
                <span>Checklists illimitées (au lieu de 1)</span>
              </li>
              <li className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-nautic-gold flex-shrink-0" />
                <span>Assistant nautique 20 questions/jour (au lieu de 1)</span>
              </li>
              <li className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-nautic-gold flex-shrink-0" />
                <span>Accès à toutes les fonctionnalités avancées</span>
              </li>
            </ul>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-4">
          <Button 
            onClick={handleSubscribe}
            className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold"
          >
            <Crown className="h-4 w-4 mr-2" />
            Découvrir l'abonnement Premium
          </Button>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="w-full"
          >
            Plus tard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumUpgradePopup;
